import React from 'react'
import { Link } from 'react-router-dom'
import { Rocket, Flame } from 'lucide-react'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'
import type { KPIInfo } from '@/components/shared/KPICard'

export interface NewLaunchRow {
  projectId: number
  projectName: string
  city: string | null
  district: string | null
  developer: string | null
  broker: string | null
  listingDate: string | null
  units: number
  avgSqmPrice: number
  typologies: string[]
}

export interface SelloutEventRow {
  projectId: number
  projectName: string
  city: string | null
  district: string | null
  developer: string | null
  broker: string | null
  soldOutDate: string | null
  totalUnits: number
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  // Keep it compact: "21 Apr"
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
  } catch {
    return iso
  }
}

function formatSqmPrice(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—'
  return `€${Math.round(n).toLocaleString('en-US')}/m²`
}

function SectionCard({
  icon,
  title,
  subtitle,
  info,
  children,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  info?: KPIInfo
  children: React.ReactNode
}) {
  return (
    <div className="card flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 rounded-lg bg-primary-50 p-2 text-primary-600">{icon}</div>
          <div>
            <h3 className="text-base font-semibold text-secondary-900">{title}</h3>
            <p className="text-xs text-secondary-500">{subtitle}</p>
          </div>
        </div>
        {info && <ChartInfoButton title={title} info={info} />}
      </div>
      {children}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex-1 flex items-center justify-center text-sm text-secondary-400 py-8">
      {message}
    </div>
  )
}

function NewLaunchesCard() {
  const { data, isLoading, isError } = useAnalyticsQuery<NewLaunchRow[]>(
    'market-new-launches',
    '/analytics/market/new-launches',
  )

  return (
    <SectionCard
      icon={<Rocket className="h-5 w-5" />}
      title="New launches"
      subtitle="Competitive projects that went live in this period — sorted by launch €/m² desc."
      info={{
        definition:
          'Projects that entered the market during the selected period — the competitive supply injection. Use to spot who is launching where, at what price, and at what scale.',
        formula:
          'Projects whose first unit listing_date falls within period_start..period_end, aggregated to project level (avg launch €/m², unit count, typologies).',
        calculation:
          'Launch €/m² = avg(current_price / private_gpa) across the project\'s units at first-listing time, trimmed to 500–25k €/m². Honors the active filter bar (city, segment, typology, etc.). Sorted by launch €/m² desc so the top-of-market launches appear first.',
        source: 'unit.listing_date, unit.current_price, unit.private_gpa, project',
      }}
    >
      {isError ? (
        <EmptyState message="Failed to load new launches." />
      ) : isLoading || !data ? (
        <EmptyState message="Loading…" />
      ) : data.length === 0 ? (
        <EmptyState message="No new launches in this period." />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-left text-xs text-secondary-500 border-b border-secondary-100">
                <th className="py-2 pr-3 font-medium">Project</th>
                <th className="py-2 pr-3 font-medium">City</th>
                <th className="py-2 pr-3 font-medium">Developer</th>
                <th className="py-2 pr-3 font-medium">Typology</th>
                <th className="py-2 pr-3 font-medium text-right">Units</th>
                <th className="py-2 pr-3 font-medium text-right">Launch €/m²</th>
                <th className="py-2 pr-3 font-medium text-right">Listed</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.projectId}
                  className="border-b border-secondary-50 last:border-0 hover:bg-secondary-50/60"
                >
                  <td className="py-2 pr-3">
                    <Link
                      to={`/projects/${row.projectId}`}
                      className="text-primary-700 hover:text-primary-900 font-medium"
                    >
                      {row.projectName || `#${row.projectId}`}
                    </Link>
                  </td>
                  <td className="py-2 pr-3 text-secondary-700">{row.city ?? '—'}</td>
                  <td className="py-2 pr-3 text-secondary-700">{row.developer ?? '—'}</td>
                  <td className="py-2 pr-3 text-secondary-700">
                    {row.typologies.length ? row.typologies.join(', ') : '—'}
                  </td>
                  <td className="py-2 pr-3 text-secondary-700 text-right">{row.units}</td>
                  <td className="py-2 pr-3 text-secondary-900 text-right font-medium">
                    {formatSqmPrice(row.avgSqmPrice)}
                  </td>
                  <td className="py-2 pr-3 text-secondary-500 text-right">
                    {formatDate(row.listingDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  )
}

function SelloutEventsCard() {
  const { data, isLoading, isError } = useAnalyticsQuery<SelloutEventRow[]>(
    'market-sellout-events',
    '/analytics/market/sellout-events',
  )

  return (
    <SectionCard
      icon={<Flame className="h-5 w-5" />}
      title="Sold-out this period"
      subtitle="Projects that transitioned to sold-out — a strong demand signal for that micro-market."
      info={{
        definition:
          'Projects that flipped to sold-out during the selected period. A clear signal of demand strength in that city / segment.',
        formula:
          'Projects where sold_out = true AND sold_out_date BETWEEN period_start AND period_end',
        calculation:
          'Sold-out state is managed by the monitoring pipeline (can be set explicitly via "Mark sold out" or inferred when the broker delists the project). totalUnits is the all-time unit count on the project.',
        source: 'project.sold_out, project.sold_out_date',
      }}
    >
      {isError ? (
        <EmptyState message="Failed to load sold-out events." />
      ) : isLoading || !data ? (
        <EmptyState message="Loading…" />
      ) : data.length === 0 ? (
        <EmptyState message="No sold-out events in this period." />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-left text-xs text-secondary-500 border-b border-secondary-100">
                <th className="py-2 pr-3 font-medium">Project</th>
                <th className="py-2 pr-3 font-medium">City</th>
                <th className="py-2 pr-3 font-medium">Developer</th>
                <th className="py-2 pr-3 font-medium">Broker</th>
                <th className="py-2 pr-3 font-medium text-right">Units</th>
                <th className="py-2 pr-3 font-medium text-right">Sold-out</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.projectId}
                  className="border-b border-secondary-50 last:border-0 hover:bg-secondary-50/60"
                >
                  <td className="py-2 pr-3">
                    <Link
                      to={`/projects/${row.projectId}`}
                      className="text-primary-700 hover:text-primary-900 font-medium"
                    >
                      {row.projectName || `#${row.projectId}`}
                    </Link>
                  </td>
                  <td className="py-2 pr-3 text-secondary-700">{row.city ?? '—'}</td>
                  <td className="py-2 pr-3 text-secondary-700">{row.developer ?? '—'}</td>
                  <td className="py-2 pr-3 text-secondary-700">{row.broker ?? '—'}</td>
                  <td className="py-2 pr-3 text-secondary-700 text-right">{row.totalUnits}</td>
                  <td className="py-2 pr-3 text-secondary-500 text-right">
                    {formatDate(row.soldOutDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  )
}

/**
 * "This week in the market" — competitive market activity panel rendered on
 * the analytics overview. Shows two side-by-side cards:
 *   1. New launches (projects that went live in the active period)
 *   2. Sold-out events (projects that transitioned to sold-out in the period)
 *
 * Both cards honor the active filter context (period + all scope filters) via
 * the shared `useAnalyticsQuery` hook.
 */
export function MarketActivityPanel() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <NewLaunchesCard />
      <SelloutEventsCard />
    </div>
  )
}
